import { Box, Grid, Typography, Button } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { InfoCard } from "src/design";
import { colorTextPrimary, colorTextMuted, colorWarning, radiusButton, radiusInput } from "src/design/tokens";
import LeaveModal, { LeaveToggleEvent } from "src/components/Main/Pages/Schedule/components/LeavesModal";

interface LeaveDaysSectionProps {
  /** Display leaves already merged with pending state (computed in parent). */
  leaves: string[];
  editing: boolean;
  restaurantId: string;
  pendingAdds: string[];
  pendingDeleteDates: string[];
  onToggle: (event: LeaveToggleEvent) => void;
}

export const LeaveDaysSection = ({
  leaves,
  editing,
  restaurantId,
  pendingAdds,
  pendingDeleteDates,
  onToggle,
}: LeaveDaysSectionProps) => {
  const { t } = useTranslation();
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);

  return (
    <Grid item>
      <InfoCard
        title={t("schedule.leaveDays")}
        subtitle={t("schedule.leaveDaysSub")}
        icon={<EventBusyOutlinedIcon fontSize="small" />}
        action={
          editing ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddCircleOutlineIcon fontSize="small" />}
              onClick={() => setLeaveModalOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: radiusButton,
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            >
              {t("schedule.manageLeaves")}
            </Button>
          ) : undefined
        }
      >
        {leaves.length > 0 ? (
          leaves.map((date) => (
            <Box
              key={date}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderRadius: radiusInput,
                backgroundColor: "rgba(245, 158, 11, 0.08)",
                border: "1px solid rgba(245, 158, 11, 0.2)",
                mb: 1.5,
              }}
            >
              <EventBusyOutlinedIcon sx={{ color: colorWarning }} />
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: colorTextPrimary }}
                >
                  {t("schedule.leaveDay")}
                </Typography>
                <Typography variant="caption" sx={{ color: colorTextMuted }}>
                  {date}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <Typography
            variant="body2"
            sx={{ color: colorTextMuted, fontStyle: "italic" }}
          >
            {t("schedule.noLeaves")}
          </Typography>
        )}
      </InfoCard>

      <LeaveModal
        open={leaveModalOpen}
        onClose={() => setLeaveModalOpen(false)}
        restaurantId={restaurantId}
        pendingAdds={pendingAdds}
        pendingDeleteDates={pendingDeleteDates}
        onToggle={onToggle}
      />
    </Grid>
  );
};
